/* eslint-disable @typescript-eslint/no-explicit-any */
import type { BaseEntityLoad, Loaders } from './definition';

function remapLoaderConfig<T extends BaseEntityLoad>({
  load,
  defaultParser,
  parseError,
}: T): {
  action: T['load'];
  parseError?: T['parseError'];
  messages: { suppressSuccess: boolean };
} {
  return {
    parseError,

    action: async (params) => {
      const result = await load(params);

      if (!defaultParser) return result;

      return defaultParser(result);
    },

    messages: {
      suppressSuccess: true,
    },
  };
}

function remapAsMutations<T extends Loaders<any>>(loaders: T) {
  return Object.fromEntries(
    Object.entries(loaders).map(([sub, config]) => [sub, remapLoaderConfig(config)]),
  );
}

export type LoaderAsMutations<TargetLoaders extends Record<string, Loaders<any>>> = {
  [Module in keyof TargetLoaders]: {
    [SubModule in keyof TargetLoaders[Module]]: {
      action: TargetLoaders[Module][SubModule]['load'];
      parseError?: TargetLoaders[Module][SubModule]['parseError'];
      messages: { suppressSuccess: boolean };
    };
  };
};

export function loadersAsMutations<TargetLoaders extends Record<string, Loaders<any>>>(
  loaders: TargetLoaders,
): LoaderAsMutations<TargetLoaders> {
  return Object.fromEntries(
    Object.entries(loaders).map(([loadModule, moduleLoaders]) => [
      loadModule,
      remapAsMutations(moduleLoaders),
    ]),
  ) as LoaderAsMutations<TargetLoaders>;
}
